// Imports
import { Injectable } from '@angular/core';
import { BundleCall } from '@ispm-client/modules/aggregator/services';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { Store } from '@ngrx/store';
import { get, isEmpty } from 'lodash';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { MDTTypePayload, MDTTypePayloadTypeConfig } from '../interfaces/mdt-type-map.interface';
import { UPDATE_SELECTED_MDT_TYPE_CONFIG } from '../store/current-mdt-configurations.reducer';

@Injectable()
export class MDTDecisionTreeService {

  private organizationId: string = '';
  private defaultMDTTypeConfigurationsCache$: Observable<MDTTypePayload>;
  private organizationMDTTypeConfigurationsCache$: Observable<MDTTypePayload>;

  constructor(
    private _bundleCall: BundleCall,
    private genomicsStore: Store<IgenomicsState>
  ) {
    this.genomicsStore.select('users').take(1).subscribe(data => {
      if (data && data.currentUser) {
        this.organizationId = data.currentUser.organization;
      }
    });
  }

  /**
   * Creates MDT config request payload
   * @param context holds context
   * @param resourceName holds the name of the resource to be fetched
   */
  private createMDTConfigurationRequestPayload(context: string, resourceName: string) {
    return {
      'queries': [{
        'targetResource': 'AdditionalConfiguration',
        'queryString': `context=${context}&name=${resourceName}`
      }]
    }
  }

  /**
   * Returns all MDT configuration
   * @param name name of resource location
   * @returns Observable<MDTTypePayload[]>
   */
  public getAllMDTConfiguration(name: string): Observable<MDTTypePayload[]> {
    if(!this.defaultMDTTypeConfigurationsCache$) {
      this.defaultMDTTypeConfigurationsCache$ = this._bundleCall.searchCall(this.createMDTConfigurationRequestPayload('Default', name)).pipe(
        shareReplay(1));
      this.organizationMDTTypeConfigurationsCache$ = this._bundleCall.searchCall(this.createMDTConfigurationRequestPayload(this.organizationId, name)).pipe(
        shareReplay(1));
    }

    return Observable.combineLatest([this.defaultMDTTypeConfigurationsCache$, this.organizationMDTTypeConfigurationsCache$]);
  }

  /**
   * Process duplicate MDT entries
   * @param mdtTypes mdtType contains list mdt configuration
   */
  public processDuplicateMDTType(mdtTypes: MDTTypePayloadTypeConfig[]): MDTTypePayloadTypeConfig[] {

    let combinedConfig: MDTTypePayloadTypeConfig[] = [];

    const duplicatedEntries = mdtTypes.reduce((previousConfig, currentConfig) => {
      previousConfig[currentConfig.mdtType] = ++previousConfig[currentConfig.mdtType] || 0;
      return previousConfig;
    }, {});

    if (duplicatedEntries && !isEmpty(duplicatedEntries)) {
      const duplicatedKeys = Object.keys(duplicatedEntries);

      duplicatedKeys.map((key) => {
        const duplicateConfig = mdtTypes.filter((mdtType) => mdtType.mdtType === key);
        let cancerTypes: string = '';
        duplicateConfig.map((config: MDTTypePayloadTypeConfig) => {
          cancerTypes = cancerTypes.concat(',', config.cancerType);
        });
        cancerTypes = cancerTypes.slice(1);
        combinedConfig.push({
          cancerType: cancerTypes,
          clinicalPhase: duplicateConfig[0].clinicalPhase,
          kb: duplicateConfig[0].kb,
          mdtType: duplicateConfig[0].mdtType,
          mdtTypeDispaly: duplicateConfig[0].mdtTypeDispaly,
          mdtTypeGroup: duplicateConfig[0].mdtTypeGroup
        });
      });
      return combinedConfig;
    }
    return mdtTypes;
  }

  /**
   * Stores cuurent mdt configuration to the store
   */
  public storeCurrentMDTConfig(mdtType: string, cancerTypeCode: string) {
    let combinedConfig: MDTTypePayloadTypeConfig[] = [];
    this.getAllMDTConfiguration('MDT-CANCER-TYPE-GROUP').subscribe((config) => {
      const defaultConfig = get(config[0],'[0].response.entry.0.resource.payload',{});
      const organizationConfig = get(config[1],'[0].response.entry.0.resource.payload',{});
      if(defaultConfig) {
        combinedConfig = (organizationConfig && !isEmpty(organizationConfig.typeConfig)) ? organizationConfig.typeConfig.concat(defaultConfig.typeConfig) 
                                : defaultConfig.typeConfig;
        this.findMDTConfig(combinedConfig, mdtType, cancerTypeCode);
      }
    });
  }

  /**
   * Finds current mdt object out of list
   * @param config holds list of configs
   */
  private findMDTConfig(config: MDTTypePayloadTypeConfig[], mdtType: string, cancerTypeCode: string) {
    const payload: MDTTypePayloadTypeConfig[] = config.filter((config: MDTTypePayloadTypeConfig) => {
      return ( config.mdtType === mdtType );
    });
    let mdtPayload: MDTTypePayloadTypeConfig | null = (!isEmpty(payload) ? payload.find((config: MDTTypePayloadTypeConfig) => this.findCancerType(config, cancerTypeCode)) : null);
    mdtPayload = mdtPayload ?? payload[0];
    mdtPayload.currentCancerCode = cancerTypeCode ?? '';
    this.storeSelectedMDTtypeConfig(mdtPayload);
  }

  /**
   * checks if given cancer type is present
   * @param config holds all mdt configuration
   * @param cancerTypeCode selected cancer type code
   * @returns boolean
   */
  private findCancerType(config: MDTTypePayloadTypeConfig, cancerTypeCode: string): boolean {
    const cancerTypes: string[] = config.cancerType.split(',');
    let selectedCancerType: string = cancerTypeCode;
    let isFound: boolean[] = [];

    if(selectedCancerType.includes('.')) {
      while(!selectedCancerType.endsWith(".")) {
        isFound.push(cancerTypes.includes(selectedCancerType));
        selectedCancerType = selectedCancerType.slice(0, -1);
      }
    }
    isFound.push(cancerTypes.includes(selectedCancerType.split('.')[0]));

    return isFound.includes(true);
  }

  /**
   * Stores config to store
   * @param payload holds payload to store
   */
  public storeSelectedMDTtypeConfig(payload: MDTTypePayloadTypeConfig) {
    // Storing the selected MDT type configuration to store
      this.genomicsStore.dispatch({
        type: UPDATE_SELECTED_MDT_TYPE_CONFIG,
        payload: payload
      });
  }
}